import { fmtEUR } from "../lib/auth";
import type { GoalNode } from "../api/client";
import SubGoalTile from "./SubGoalTile";

interface Props {
  root: GoalNode;
  /** show level-2 sub-sub-goals as small pills under each child */
  expanded?: boolean;
  /** show the world-globe pictogram next to the umbrella */
  showGlobe?: boolean;
}

const NODE_W = 132;
const NODE_H = 66;
const ROOT_W = 200;
const ROOT_H = 80;
const GAP_X = 12;
const ROW_GAP = 90;
const PILL_H = 30;
const PILL_GAP = 4;
const PILL_W_DEFAULT = NODE_W;

export default function SparbaumTree({ root, expanded = false, showGlobe = false }: Props) {
  return (
    <>
      {/* Desktop: SVG tree */}
      <div className="hidden md:block">
        <SvgTree root={root} expanded={expanded} showGlobe={showGlobe} />
      </div>
      {/* Mobile: vertical stacked cards */}
      <div className="md:hidden">
        <VerticalTree root={root} expanded={expanded} showGlobe={showGlobe} />
      </div>
    </>
  );
}

function SvgTree({ root, expanded = false, showGlobe = false }: Props) {
  const rootTotal = Number(root.total);
  const rootTarget = Number(root.total_target);
  const rootPct = rootTarget > 0 ? Math.min(1, rootTotal / rootTarget) : 0;
  const children = root.children ?? [];

  const childrenTotalW = children.length * NODE_W + (children.length - 1) * GAP_X;
  const minW = 40 + (showGlobe ? 110 : 0) + ROOT_W;
  const W = Math.max(minW, childrenTotalW + 40);

  const rootX = W / 2 - ROOT_W / 2;
  const rootY = 0;
  const childY = ROOT_H + ROW_GAP;
  const childStartX = (W - childrenTotalW) / 2;

  // Compute height per child (incl. expanded pills below).
  const childHeights = children.map((c) => {
    const grand = c.children?.length ?? 0;
    if (!expanded || grand === 0) return NODE_H;
    return NODE_H + 8 + grand * (PILL_H + PILL_GAP);
  });
  const maxChildHeight = Math.max(NODE_H, ...childHeights);
  const H = ROOT_H + ROW_GAP + maxChildHeight + 16;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* connectors first */}
      {children.map((c, i) => {
        const cx = childStartX + i * (NODE_W + GAP_X) + NODE_W / 2;
        const cTotal = Number(c.total);
        const cTarget = Number(c.total_target);
        const cPct = cTarget > 0 ? cTotal / cTarget : 0;
        const ax = rootX + ROOT_W / 2;
        const ay = rootY + ROOT_H;
        const by = childY;
        return (
          <path
            key={`l${c.id}`}
            d={`M ${ax} ${ay} C ${ax} ${(ay + by) / 2}, ${cx} ${(ay + by) / 2}, ${cx} ${by}`}
            fill="none"
            stroke={cPct > 0 ? "rgba(20,184,166,0.55)" : "rgba(148,163,184,0.25)"}
            strokeWidth="1.5"
          />
        );
      })}

      {/* Globe (left of umbrella) */}
      {showGlobe && (
        <g transform={`translate(${rootX - 92}, ${rootY - 4})`}>
          <GlobeSvg size={88} />
        </g>
      )}

      {/* Root */}
      <Node
        x={rootX}
        y={rootY}
        w={ROOT_W}
        h={ROOT_H}
        title={root.name}
        amount={fmtEUR(rootTotal)}
        target={fmtEUR(rootTarget)}
        pct={rootPct}
        emphasis
      />

      {/* Children */}
      {children.map((c, i) => {
        const x = childStartX + i * (NODE_W + GAP_X);
        const cTotal = Number(c.total);
        const cTarget = Number(c.total_target);
        const cPct = cTarget > 0 ? cTotal / cTarget : 0;
        const grand = c.children ?? [];
        return (
          <g key={c.id}>
            <Node
              x={x}
              y={childY}
              w={NODE_W}
              h={NODE_H}
              title={c.name}
              amount={fmtEUR(cTotal)}
              target={fmtEUR(cTarget)}
              pct={cPct}
              hasChildren={grand.length > 0}
            />
            {expanded &&
              grand.map((gc, j) => {
                const gx = x;
                const gy = childY + NODE_H + 8 + j * (PILL_H + PILL_GAP);
                return (
                  <SubPill
                    key={gc.id}
                    x={gx}
                    y={gy}
                    w={PILL_W_DEFAULT}
                    name={gc.name}
                    amount={fmtEUR(Number(gc.total))}
                    pct={Number(gc.total_target) > 0 ? Number(gc.total) / Number(gc.total_target) : 0}
                  />
                );
              })}
          </g>
        );
      })}
    </svg>
  );
}

interface NodeProps {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  amount: string;
  target: string;
  pct: number;
  emphasis?: boolean;
  hasChildren?: boolean;
}

function Node({ x, y, w, h, title, amount, target, pct, emphasis, hasChildren }: NodeProps) {
  const clamped = Math.max(0, Math.min(1, pct));
  const fillH = h * clamped;
  const fillY = h - fillH;
  // unique ids per node so multiple Nodes' clip-paths don't collide
  const clipId = `wave-clip-${Math.round(x)}-${Math.round(y)}`;

  // Wave amplitude scales with node size; flatten when fill is near edges
  const amp = clamped > 0.02 && clamped < 0.98 ? Math.min(3, h * 0.04) : 0;

  // 4 troughs across width
  const seg = w / 4;
  const baseY = fillY;
  const wavePath =
    `M 0 ${baseY}` +
    ` Q ${seg / 2} ${baseY - amp}, ${seg} ${baseY}` +
    ` T ${seg * 2} ${baseY}` +
    ` T ${seg * 3} ${baseY}` +
    ` T ${seg * 4} ${baseY}` +
    ` V ${h} H 0 Z`;

  return (
    <g transform={`translate(${x}, ${y})`}>
      <defs>
        <clipPath id={clipId}>
          <rect rx="10" ry="10" width={w} height={h} />
        </clipPath>
      </defs>

      <rect
        rx="10"
        ry="10"
        width={w}
        height={h}
        fill={emphasis ? "#0f766e" : "#0f172a"}
        stroke={emphasis ? "#14b8a6" : "#1e293b"}
      />

      {fillH > 0 && (
        <g clipPath={`url(#${clipId})`}>
          {/* Body of the water, slightly translucent */}
          <path
            d={wavePath}
            fill={emphasis ? "rgba(20,184,166,0.38)" : "rgba(20,184,166,0.22)"}
          />
          {/* Crisp wave line on top */}
          <path
            d={
              `M 0 ${baseY}` +
              ` Q ${seg / 2} ${baseY - amp}, ${seg} ${baseY}` +
              ` T ${seg * 2} ${baseY}` +
              ` T ${seg * 3} ${baseY}` +
              ` T ${seg * 4} ${baseY}`
            }
            fill="none"
            stroke={emphasis ? "rgba(45,212,191,0.7)" : "rgba(45,212,191,0.5)"}
            strokeWidth="1"
          />
        </g>
      )}

      <text x={w / 2} y={emphasis ? 22 : 18} textAnchor="middle" fontSize={emphasis ? 14 : 12} fontWeight={600} fill="#f8fafc">
        {title}
        {hasChildren && <tspan fill="#94a3b8" fontWeight={400} fontSize={10}> …</tspan>}
      </text>
      <text className="num" x={w / 2} y={emphasis ? 44 : 36} textAnchor="middle" fontSize={emphasis ? 13 : 11} fill="#e2e8f0" style={{ fontVariantNumeric: "tabular-nums" }}>
        {amount}
      </text>
      <text className="num" x={w / 2} y={emphasis ? 64 : 52} textAnchor="middle" fontSize={emphasis ? 11 : 10} fill="#94a3b8" style={{ fontVariantNumeric: "tabular-nums" }}>
        / {target} · {(pct * 100).toFixed(0)} %
      </text>
    </g>
  );
}

function SubPill({
  x,
  y,
  w,
  name,
  amount,
  pct,
}: {
  x: number;
  y: number;
  w: number;
  name: string;
  amount: string;
  pct: number;
}) {
  const fillW = Math.max(0, Math.min(w, w * pct));
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect rx="6" ry="6" width={w} height={PILL_H} fill="#0b1322" stroke="#1e293b" />
      {fillW > 0 && (
        <rect
          rx="6"
          ry="6"
          width={fillW}
          height={PILL_H}
          fill="rgba(20,184,166,0.18)"
        />
      )}
      {/* Name (top line) */}
      <text x={6} y={11} fontSize={9.5} fontWeight={500} fill="#e2e8f0">
        {truncate(name, 18)}
      </text>
      {/* Amount (bottom line) */}
      <text
        className="num"
        x={6}
        y={23}
        fontSize={9}
        fill="#94a3b8"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {amount}
      </text>
      {/* Percent (bottom right) */}
      <text
        className="num"
        x={w - 6}
        y={23}
        fontSize={9}
        textAnchor="end"
        fill="#64748b"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {(pct * 100).toFixed(0)} %
      </text>
    </g>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

/**
 * Mobile-friendly vertical layout — drops the umbrella card and shows each
 * sub-goal as a wave-fill tile (same as the dashboard subgoal grid). Cards
 * are stitched together with subtle vertical guide lines so the tree
 * structure stays implied.
 */
function VerticalTree({ root, expanded = false }: Props) {
  const kids = root.children ?? [];
  // A single child + a vertical guide line looks like a stranded branch —
  // skip the tree decoration entirely and let the tile breathe full-width.
  if (kids.length === 1) {
    return (
      <div>
        <SubGoalTile node={kids[0]} />
      </div>
    );
  }
  return (
    <div className="relative pl-4">
      {/* Continuous vertical guide line on the left */}
      <div
        aria-hidden="true"
        className="absolute left-1.5 top-2 bottom-2 w-px bg-gradient-to-b from-brand-500/60 via-ink-700/60 to-transparent"
      />
      <ul className="space-y-3">
        {kids.map((c) => {
          const grand = c.children ?? [];
          return (
            <li key={c.id} className="relative">
              {/* Branch dot */}
              <span
                aria-hidden="true"
                className="absolute -left-3 top-6 h-2 w-2 rounded-full bg-brand-500/70 ring-2 ring-ink-950"
              />
              <SubGoalTile node={c} />
              {expanded && grand.length > 0 && (
                <ul className="relative mt-2 ml-3 pl-4 space-y-1">
                  <div
                    aria-hidden="true"
                    className="absolute left-0 top-1 bottom-1 w-px bg-ink-700/60"
                  />
                  {grand.map((gc) => {
                    const gTotal = Number(gc.total);
                    const gTarget = Number(gc.total_target);
                    const gPct =
                      gTarget > 0 ? Math.min(100, (gTotal / gTarget) * 100) : 0;
                    return (
                      <li
                        key={gc.id}
                        className="relative flex items-center gap-2 rounded-md border border-ink-800 bg-ink-900/60 px-2.5 py-1.5 text-xs"
                      >
                        <span
                          aria-hidden="true"
                          className="absolute -left-2 top-1/2 h-px w-2 bg-ink-700"
                        />
                        <span className="text-ink-300 truncate">{gc.name}</span>
                        <span className="ml-auto num text-ink-400">
                          {fmtEUR(gTotal)}{" "}
                          <span className="text-ink-500">/ {fmtEUR(gTarget)}</span>
                        </span>
                        <span className="num text-ink-500 w-9 text-right">
                          {gPct.toFixed(0)} %
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Inline globe used inside the Sparbaum SVG, next to the umbrella.
 * Same look as Globe.tsx but parameterised by size.
 */
function GlobeSvg({ size }: { size: number }) {
  const r = size / 2;
  return (
    <g>
      <circle cx={r} cy={r} r={r * 0.85} fill="#0e7490" fillOpacity="0.18" stroke="#22d3ee" strokeOpacity="0.5" strokeWidth="0.7" />
      <g stroke="#94a3b8" strokeOpacity="0.2" fill="none" strokeWidth="0.5">
        <ellipse cx={r} cy={r} rx={r * 0.85} ry={r * 0.25} />
        <ellipse cx={r} cy={r} rx={r * 0.85} ry={r * 0.5} />
        <ellipse cx={r} cy={r} rx={r * 0.25} ry={r * 0.85} />
        <ellipse cx={r} cy={r} rx={r * 0.5} ry={r * 0.85} />
      </g>
      <line x1={r * 0.15} y1={r} x2={r * 1.85} y2={r} stroke="#22d3ee" strokeOpacity="0.4" strokeWidth="0.7" />
      <g fill="#94a3b8" fillOpacity="0.55" transform={`scale(${size / 120}) translate(0,0)`}>
        <path d="M28 36 Q 24 50, 30 60 Q 28 72, 34 84 Q 36 92, 30 96 Q 24 88, 22 76 Q 18 62, 22 52 Q 24 42, 28 36 Z" />
        <path d="M58 28 Q 64 32, 64 42 Q 70 44, 70 52 Q 68 64, 60 74 Q 56 80, 54 76 Q 50 70, 52 60 Q 50 50, 54 42 Q 54 34, 58 28 Z" />
        <path d="M76 32 Q 90 30, 96 42 Q 98 50, 94 56 Q 90 60, 84 58 Q 80 56, 78 50 Q 74 42, 76 32 Z" />
        <path d="M86 70 Q 96 70, 98 78 Q 96 84, 88 84 Q 82 80, 86 70 Z" />
      </g>
      <path
        d={`M ${r * 0.32} ${r * 1.12} C ${r * 0.18} ${r * 1.12}, ${r * 0.18} ${r * 0.92}, ${r * 0.5} ${r * 0.88} C ${r} ${r * 0.84}, ${r * 1.4} ${r * 0.92}, ${r * 1.6} ${r}, C ${r * 1.85} ${r * 1.12}, ${r * 1.5} ${r * 1.18}, ${r * 0.96} ${r * 1.16} Z`}
        fill="none"
        stroke="#ef4444"
        strokeWidth={size / 60}
        strokeDasharray="3 2"
        strokeLinecap="round"
      />
      <circle cx={r * 1.05} cy={r * 0.95} r={size / 36} fill="#ef4444" />
    </g>
  );
}
